import { useMoney } from '../context/LocationContext';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FlaskConical, PlayCircle, Loader2, ArrowRight, BookOpen, Database,
  CheckCircle2, AlertTriangle, Receipt,
} from 'lucide-react';

/**
 * FR-7 Test-Sync Dry Run.
 * Merchants preview exactly what SyncPoint would post to QBO/Odoo for their
 * most recent completed sales, side by side. Optionally posts as Odoo drafts.
 */
export default function DryRun() {
  const { token } = useAuth();
  const [sampleSize, setSampleSize] = useState(5);
  const [postOdooDrafts, setPostOdooDrafts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.onboardingDryRun(token, sampleSize, postOdooDrafts);
      setResult(r.data);
      if (!r.data.sales?.length) {
        toast.info(r.data.message || 'No completed sales yet');
      } else {
        toast.success(`Previewed ${r.data.sales.length} sale${r.data.sales.length>1?'s':''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dry-run-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
          <FlaskConical className="w-6 h-6 text-blue-300"/>
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">Test-sync (dry run)</h1>
          <p className="text-slate-400 mt-1 max-w-2xl">
            See exactly what SyncPoint would post to your accounting systems <em>before</em> flipping the switch. Nothing hits QuickBooks. Odoo drafts stay in draft.
          </p>
        </div>
      </div>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-base font-heading text-white">Run configuration</CardTitle>
          <CardDescription className="text-slate-500">Pulls your most recent completed UnTill receipts and shows the mapped output.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">Sample size</label>
              <Select
                value={String(sampleSize)}
                onValueChange={(v)=>setSampleSize(parseInt(v, 10))}
              >
                <SelectTrigger
                  className="w-full bg-slate-950 border-slate-800 text-white"
                  data-testid="dry-run-sample-size"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-white">
                  {[3, 5, 10, 20].map((n)=>(
                    <SelectItem key={n} value={String(n)} className="focus:bg-slate-800 focus:text-white">
                      {n} most recent receipts
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">Odoo behaviour</label>
              <label className="flex items-start gap-2 text-sm text-slate-200 cursor-pointer select-none bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={postOdooDrafts}
                  onChange={(e)=>setPostOdooDrafts(e.target.checked)}
                  className="mt-0.5 rounded border-slate-700 bg-slate-950"
                  data-testid="dry-run-odoo-drafts"
                />
                <span>
                  <span className="text-white">Also post as Odoo drafts</span>
                  <span className="block text-xs text-slate-400 mt-0.5">
                    Creates real <code className="text-purple-300 font-mono">sale.order</code> rows in <span className="text-emerald-300">draft</span> state. Safe to inspect and delete in Odoo without touching revenue.
                  </span>
                </span>
              </label>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Button onClick={run} disabled={loading} className="bg-blue-500 hover:bg-blue-500/90 text-white" data-testid="dry-run-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <PlayCircle className="w-4 h-4 mr-2"/>}
              Run dry-run
            </Button>
            {result && (
              <div className="text-xs text-slate-400 flex items-center gap-3">
                {result.qbo_connected
                  ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30"><BookOpen className="w-3 h-3 mr-1"/> QuickBooks connected</Badge>
                  : <Badge className="bg-slate-800 text-slate-400 border-slate-700">QuickBooks not connected</Badge>}
                {result.odoo_connected
                  ? <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30"><Database className="w-3 h-3 mr-1"/> Odoo connected</Badge>
                  : <Badge className="bg-slate-800 text-slate-400 border-slate-700">Odoo not connected</Badge>}
                {result.posted_odoo_drafts && (
                  <Badge className="bg-purple-500/10 text-purple-300 border-purple-500/30">Odoo drafts posted</Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {result?.sales?.length > 0 && (
        <div className="space-y-4">
          {result.sales.map((row) => (
            <PreviewRow key={row.sale.id} row={row}/>
          ))}
        </div>
      )}

      {result && !result.sales?.length && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-8 text-center text-slate-400">
            <Receipt className="w-8 h-8 mx-auto mb-3 opacity-40"/>
            {result.message || 'No completed sales yet.'}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PreviewRow({ row }) {
  const s = row.sale;
  const { money } = useMoney();
  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid={`dry-run-row-${s.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-slate-300"/>
            <CardTitle className="text-white text-base font-heading">{s.receipt_number || s.id}</CardTitle>
            <span className="text-xs text-slate-500 font-mono">{s.created_at?.slice(0,10)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">{s.payment_method || '—'}</span>
            <span className="text-xl font-bold font-mono text-white">{money(s.total, s.location_id)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SystemPreview
            icon={<BookOpen className="w-4 h-4"/>}
            title="QuickBooks Online"
            accent="blue"
            preview={row.qbo}
            locationId={s.location_id}
          />
          <SystemPreview
            icon={<Database className="w-4 h-4"/>}
            title="Odoo ERP"
            accent="purple"
            preview={row.odoo}
            locationId={s.location_id}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SystemPreview({ icon, title, accent, preview, locationId }) {
  const { money } = useMoney();
  const border = accent === 'blue' ? 'border-blue-500/20' : 'border-purple-500/20';
  const iconColor = accent === 'blue' ? 'text-blue-300 bg-blue-500/10 border-blue-500/30' : 'text-purple-300 bg-purple-500/10 border-purple-500/30';

  if (!preview.available) {
    return (
      <div className={`rounded-lg border ${border} bg-slate-950/40 p-4`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center border ${iconColor}`}>{icon}</div>
          <div className="text-sm font-medium text-white">{title}</div>
          <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">skipped</Badge>
        </div>
        <div className="text-xs text-slate-400 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400 flex-shrink-0"/>
          {preview.reason || 'Not available'}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${border} bg-slate-950/40 p-4`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center border ${iconColor}`}>{icon}</div>
        <div className="text-sm font-medium text-white">{title}</div>
        <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]">
          <CheckCircle2 className="w-3 h-3 mr-1"/>{preview.entity || 'preview'}
        </Badge>
        {preview.state && (
          <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px]">state: {preview.state}</Badge>
        )}
        {preview.posted_draft_id && (
          <Badge className="bg-purple-500/10 text-purple-300 border-purple-500/30 text-[10px]" data-testid="odoo-draft-id">
            draft #{preview.posted_draft_id}
          </Badge>
        )}
      </div>
      <div className="space-y-1.5 text-xs">
        {preview.company && <Field label="Company" value={preview.company}/>}
        {preview.customer && <Field label="Customer" value={preview.customer}/>}
        {preview.partner && <Field label="Partner" value={preview.partner}/>}
        {preview.item && <Field label="Item" value={preview.item}/>}
        {preview.product_line && <Field label="Line" value={preview.product_line}/>}
        {preview.deposit_account && <Field label="Deposit account" value={preview.deposit_account}/>}
        {preview.payment_method && <Field label="Payment method" value={preview.payment_method}/>}
        {preview.txn_date && <Field label="Txn date" value={preview.txn_date}/>}
        {preview.date_order && <Field label="Order date" value={preview.date_order}/>}
        {'total_amount' in preview && <Field label="Total" mono value={money(preview.total_amount, locationId)}/>}
        {'amount_total' in preview && <Field label="Amount" mono value={money(preview.amount_total, locationId)}/>}
      </div>
      {preview.draft_error && (
        <div className="mt-3 text-xs text-rose-300 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>{preview.draft_error}
        </div>
      )}
      {preview.note && (
        <div className="mt-3 text-[11px] text-slate-500 border-t border-slate-800 pt-2 flex items-start gap-1.5">
          <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0"/>{preview.note}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-slate-500 min-w-[110px]">{label}</span>
      <span className={`text-slate-200 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}
